import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, PLATFORM_ID, afterNextRender, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AppPageBase, AuthService, LsSearchBoxComponent, RecueilService, TrendingChartsComponent } from '@lyricstream/shared';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LyricListSectionComponent } from '../../ui/lyric-list-section/lyric-list-section.component';
import { RecueilGridComponent } from '../../ui/recueil-grid/recueil-grid.component';

/** Durée d'affichage de chaque image du héros (ms). */
const HERO_ROTATION_MS = 8000;
/** Durée de la disparition du texte avant son remplacement (= transition d'opacité de `.hero-caption`). */
const HERO_CAPTION_FADE_MS = 900;

@Component({
  selector: 'app-home',
  imports: [RouterLink, RecueilGridComponent, LyricListSectionComponent, TrendingChartsComponent, TranslateModule, LsSearchBoxComponent],
  templateUrl: './home.html',
  styleUrl: './home.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent extends AppPageBase implements OnDestroy {
  // protected readonly lang = inject(LANG_SIGNAL);
  private  readonly platformId = inject(PLATFORM_ID);
  private  readonly translate   = inject(TranslateService);
  private  readonly auth        = inject(AuthService);

  /** Les blocs d'invitation à s'inscrire (« Pour qui ? », bandeau final) sont masqués pour un utilisateur connecté. */
  protected readonly isConnected = this.auth.isAuthenticated;

  // ── Recueils (hymnals) ───────────────────────────────
  protected readonly recueilService = inject(RecueilService);

  protected readonly recueils        = this.recueilService.recueils;
  protected readonly recueilsLoading = this.recueilService.loading;
  protected readonly recueilsError   = this.recueilService.error;

  // ── Hero : images plein écran en fondu lent ───────────────────
  // Chemins sans suffixe : `-960.webp` / `-1920.webp` sont ajoutés dans le template (srcset).
  protected readonly heroImages = [
    '/assets/images/hero/hero-1',
    '/assets/images/hero/hero-2',
    '/assets/images/hero/hero-3',
    '/assets/images/hero/hero-4',
    '/assets/images/hero/hero-5',
  ];

  protected readonly activeSlide = signal(0);

  /** Textes de chaque image, dans le même ordre que `heroImages`. */
  private readonly heroCaptions = computed(() => {
    void this.lang();
    return this.translate.instant('HOME.HERO.CAPTIONS') as Array<{ kicker: string; h2: string }>;
  });

  /** Texte affiché : suit l'image avec un léger décalage, le temps que l'ancien texte s'estompe. */
  private readonly captionIndex = signal(0);
  protected readonly captionVisible = signal(true);
  private captionTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly heroCaption = computed(() => {
    const captions = this.heroCaptions();
    return captions[this.captionIndex()] ?? captions[0] ?? { kicker: '', h2: '' };
  });
  private sliderTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    super();
    afterNextRender(() => {
      if (isPlatformBrowser(this.platformId)) {
        this.recueilService.getRecueilsActive();
        this.startHeroRotation();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopHeroRotation();
  }

  /** Fondu automatique, sauf si l'utilisateur a demandé moins d'animations ; en pause quand l'onglet est caché. */
  private startHeroRotation(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stopHeroRotation();
    this.sliderTimer = setInterval(() => {
      if (document.hidden) return;
      const next = (this.activeSlide() + 1) % this.heroImages.length;
      this.activeSlide.set(next);
      this.swapCaption(next);
    }, HERO_ROTATION_MS);
  }

  private stopHeroRotation(): void {
    if (this.sliderTimer !== null) { clearInterval(this.sliderTimer); this.sliderTimer = null; }
    if (this.captionTimer !== null) { clearTimeout(this.captionTimer); this.captionTimer = null; }
  }

  /** Fondu enchaîné du texte : disparition, changement du texte, réapparition (durées dans home.scss). */
  private swapCaption(index: number): void {
    this.captionVisible.set(false);
    if (this.captionTimer !== null) clearTimeout(this.captionTimer);
    this.captionTimer = setTimeout(() => {
      this.captionIndex.set(index);
      this.captionVisible.set(true);
    }, HERO_CAPTION_FADE_MS);
  }

  /** Fait défiler la page jusqu'au contenu situé juste sous le héros. */
  protected scrollToContent(): void {
    const hero = document.querySelector<HTMLElement>('.home .hero');
    hero?.nextElementSibling?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  protected readonly numbers = computed(() => {
    void this.lang();
    const items = this.translate.instant('HOME.NUMBERS.ITEMS') as Array<{l: string}>;
    return [
      { n: '12.4', unit: 'k',  l: items[0]?.l ?? '' },
      { n: '847',  unit: '',   l: items[1]?.l ?? '' },
      { n: '3.8',  unit: 'M',  l: items[2]?.l ?? '' },
      { n: '1 200', unit: '',  l: items[3]?.l ?? '' },
    ];
  });

  protected readonly premiumFeatures = computed(() => {
    void this.lang();
    return this.translate.instant('HOME.PREMIUM.FEATURES') as Array<{title: string; sub: string}>;
  });

  protected readonly audienceCards = computed(() => {
    void this.lang();
    return this.translate.instant('HOME.AUDIENCE.CARDS') as Array<{h3: string; desc: string; features: string[]; cta: string; cssClass: string; icon: string; ctaSegment: string}>;
  });

  protected readonly showcaseModes = computed(() => {
    void this.lang();
    return this.translate.instant('HOME.SHOWCASE.MODES') as Array<{nm: string; sub: string; icon: string; shortcut: string}>;
  });

  protected readonly peekFeatures = computed(() => {
    void this.lang();
    return this.translate.instant('HOME.PEEK.FEATURES') as Array<{b: string; text: string}>;
  });

  protected readonly communityAvatars = [
    { initials: 'MJ', c1: '#1B3A8C', c2: '#FF6A1F' },
    { initials: 'PJ', c1: '#6B3FA0', c2: '#FFB68A' },
    { initials: 'SK', c1: '#1F8A5B', c2: '#7E97E6' },
    { initials: 'IN', c1: '#103E8A', c2: '#196EED' },
    { initials: 'DC', c1: '#7E2A0C', c2: '#FF924F' },
  ];
}
